// Card rows rendered in a window: only the rows near the viewport are in the DOM. Used for the
// Done tab once it grows past VIRTUALIZE_AFTER rows. Rows keep their natural height (measured);
// the list lives inside a scroller it shares with the notices above it, so its offset in that
// scroller is measured and passed as the scroll margin.
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { CardSummary } from "@shared/api";
import { SwipeableCardRow } from "@web/notify/SwipeableCardRow";

const ESTIMATED_ROW = 108;

export interface VirtualCardListProps {
  cards: readonly CardSummary[];
  scrollRef: RefObject<HTMLDivElement | null>;
  selectedId: string | null;
  showAccount: boolean;
  colorIndex: (accountId: string) => number;
  accountLabel: (email: string) => string | undefined;
  now: number;
  onSelect: (card: CardSummary) => void;
}

export function VirtualCardList({
  cards,
  scrollRef,
  selectedId,
  showAccount,
  colorIndex,
  accountLabel,
  now,
  onSelect,
}: VirtualCardListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(0);

  // Where the list starts inside its scroller (notices and cleanups sit above it); measured again
  // whenever something above it changes size or comes and goes.
  useLayoutEffect(() => {
    const list = listRef.current;
    const scroller = scrollRef.current;
    if (!list || !scroller) return;
    const measure = () => {
      const top =
        list.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setMargin((prev) => (Math.abs(prev - top) > 1 ? top : prev));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const resize = new ResizeObserver(measure);
    const watch = () => {
      resize.disconnect();
      for (const el of [...scroller.children, ...(list.parentElement?.children ?? [])]) {
        if (el !== list) resize.observe(el);
      }
    };
    watch();
    const mutations = new MutationObserver(() => {
      watch();
      measure();
    });
    mutations.observe(scroller, { childList: true });
    if (list.parentElement) mutations.observe(list.parentElement, { childList: true });
    return () => {
      resize.disconnect();
      mutations.disconnect();
    };
  }, [scrollRef]);

  // oxlint-disable-next-line react/incompatible-library -- no React Compiler in this app
  const virtualizer = useVirtualizer({
    count: cards.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW,
    overscan: 8,
    scrollMargin: margin,
    getItemKey: (index) => cards[index]?.id ?? index,
  });

  // j/k may select a row that is not rendered yet: bring it into the window.
  const lastSelected = useRef(selectedId);
  useEffect(() => {
    if (!selectedId || lastSelected.current === selectedId) return;
    lastSelected.current = selectedId;
    const index = cards.findIndex((c) => c.id === selectedId);
    if (index >= 0) virtualizer.scrollToIndex(index, { align: "auto" });
  }, [selectedId, cards, virtualizer]);

  return (
    <div
      ref={listRef}
      data-testid="virtual-card-list"
      className="relative"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((item) => {
        const card = cards[item.index];
        if (!card) return null;
        return (
          <div
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            className="absolute inset-x-0 top-0"
            style={{ transform: `translateY(${item.start - margin}px)` }}
          >
            <SwipeableCardRow
              card={card}
              selected={card.id === selectedId}
              showAccount={showAccount}
              colorIndex={card.accountId ? colorIndex(card.accountId) : 0}
              accountLabel={card.accountEmail ? accountLabel(card.accountEmail) : undefined}
              now={now}
              onSelect={onSelect}
            />
          </div>
        );
      })}
    </div>
  );
}
